import { Card, Badge, Spin, Empty, Tag, Tooltip, Divider } from 'antd';
import { HistoryOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { getWorkflowExecutions } from '../../API/services/workflowsService';
import { WorkflowExecutionStatus } from '../../constants/enums';
import { t } from '../../i18n/i18n';
import { getStatusConfig } from '../../utils/workflowExecutionUtils';
import DebouncedButton from '../Button/Button';
import ExecutionDetailModal from './ExecutionDetailModal';
import './WorkflowExecutions.scss';

const WorkflowExecutions = ({ workflowId, collapsed = false }) => {
  const [executions, setExecutions] = useState([]);
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const fetchExecutions = async () => {
    if (!workflowId) return;

    try {
      setLoadingExecutions(true);
      const data = await getWorkflowExecutions(workflowId);
      setExecutions(data || []);
    } catch (error) {
      console.error('Error fetching workflow executions:', error);
      setExecutions([]);
    } finally {
      setLoadingExecutions(false);
    }
  };

  useEffect(() => {
    if (workflowId) {
      fetchExecutions();
    }
  }, [workflowId]);

  const handleExecutionClick = (execution) => {
    setSelectedExecution(execution);
    setModalVisible(true);
  };

  const getExecutionStats = () => {
    if (!executions.length) return null;

    const stats = {
      total: executions.length,
      completed: executions.filter((e) => e.status === WorkflowExecutionStatus.COMPLETED).length,
      failed: executions.filter((e) => e.status === WorkflowExecutionStatus.FAILED).length,
      running: executions.filter((e) => e.status === WorkflowExecutionStatus.RUNNING).length,
    };

    return stats;
  };

  const stats = getExecutionStats();

  if (collapsed) {
    if (loadingExecutions) {
      return (
        <div className="text-center p-2">
          <Spin size="small" />
        </div>
      );
    }

    if (executions.length === 0) {
      return (
        <div className="no-executions-text text-center text-muted">
          {t('ALKYMATION_NO_EXECUTIONS_SHORT')}
        </div>
      );
    }

    return (
      <div className="workflow-executions-collapsed">
        <div className="executions-title text-muted text-center">{t('ALKYMATION_EXECUTIONS')}</div>

        <div className="stats-badges d-flex justify-content-around text-center mb-2">
          <Tooltip title={t('ALKYMATION_TOTAL')}>
            <Badge count={stats.total} showZero className="badge-total" />
          </Tooltip>
          <Tooltip title={t('ALKYMATION_SUCCESSFUL')}>
            <Badge count={stats.completed} showZero className="badge-success" />
          </Tooltip>
          <Tooltip title={t('ALKYMATION_FAILED')}>
            <Badge count={stats.failed} showZero className="badge-failed" />
          </Tooltip>
        </div>
      </div>
    );
  }

  return (
    <>
      <Card
        title={
          <span>
            <HistoryOutlined className="mr-2" />
            {t('ALKYMATION_EXECUTIONS_HISTORY')}
          </span>
        }
        className="workflow-executions-card mt-3"
        size="small"
      >
        <DebouncedButton
          type="link"
          className="workflow-executions-card-button"
          icon={<ReloadOutlined />}
          onClick={fetchExecutions}
          debounceTime={500}
        >
          {t('ALKYMATION_UPDATE_LIST')}
        </DebouncedButton>
        {loadingExecutions ? (
          <div className="text-center p-3">
            <Spin size="small" />
          </div>
        ) : executions.length === 0 ? (
          <Empty
            description={t('ALKYMATION_NO_EXECUTIONS_SHORT')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            className="empty-container"
          />
        ) : (
          <>
            {stats && (
              <div className="stats-summary mb-3">
                <div className="d-flex justify-content-around text-center">
                  <div>
                    <div className="stats-label small text-muted">{t('ALKYMATION_TOTAL')}</div>
                    <Badge count={stats.total} showZero className="badge-primary" />
                  </div>
                  <div>
                    <div className="stats-label small text-muted">{t('ALKYMATION_SUCCESSFUL')}</div>
                    <Badge count={stats.completed} showZero className="badge-success" />
                  </div>
                  <div>
                    <div className="stats-label small text-muted">{t('ALKYMATION_FAILED')}</div>
                    <Badge count={stats.failed} showZero className="badge-danger" />
                  </div>
                </div>
              </div>
            )}

            <Divider className="divider-section" />

            <div className="executions-timeline">
              {executions.slice(0, 5).map((execution, index) => {
                const statusConfig = getStatusConfig(execution.status);
                const statusClass = `execution-status-${execution.status.toLowerCase()}`;
                return (
                  <div
                    key={execution.executionId || index}
                    className={`execution-item d-flex align-items-center justify-content-between p-2 mb-1 ${statusClass}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleExecutionClick(execution)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleExecutionClick(execution);
                      }
                    }}
                  >
                    <div className="d-flex align-items-center">
                      {statusConfig.icon}
                      <span className="execution-date ml-2">
                        {format(new Date(execution.startedAt), 'dd/MM HH:mm', { locale: es })}
                      </span>
                    </div>
                    <div className="d-flex align-items-center">
                      <Tag color={statusConfig.color} className="execution-status-tag mb-0 mr-1">
                        {statusConfig.text}
                      </Tag>
                      <EyeOutlined className="execution-eye-icon text-muted" />
                    </div>
                  </div>
                );
              })}
            </div>

            {executions.length > 5 && (
              <div className="showing-results text-center text-muted">
                {t('ALKYMATION_SHOWING_RESULTS')
                  .replace('{{count}}', '5')
                  .replace('{{total}}', executions.length)}
              </div>
            )}
          </>
        )}
      </Card>

      <ExecutionDetailModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        execution={selectedExecution}
      />
    </>
  );
};

export default WorkflowExecutions;
